import { asNumber, asString } from '../http/normalise';
import { Locale, asLocale } from '../tenancy/role.model';
import {
  ApiBroadcast,
  ApiEngagementEvent,
  ApiEventMetrics,
  ApiReferralLink,
  ApiStreamTicket,
  ApiVelocity,
} from './engagement.dto';
import {
  Broadcast,
  BroadcastSeverity,
  CheckInVelocity,
  EngagementAction,
  EngagementEvent,
  EventMetrics,
  MetricsStreamTicket,
  ReferralLink,
} from './engagement.model';

const ENGAGEMENT_ACTIONS: ReadonlySet<string> = new Set<string>([
  'session-bookmark',
  'document-download',
  'survey-submit',
  'push-open',
  'gallery-view',
  'chat-message',
]);

const DEFAULT_LOCALE: Locale = 'en';

function asAction(value: unknown): EngagementAction {
  if (typeof value === 'string' && ENGAGEMENT_ACTIONS.has(value)) {
    return value as EngagementAction;
  }
  return 'gallery-view';
}

export function mapEngagementEvent(dto: ApiEngagementEvent): EngagementEvent {
  return {
    id: dto.id,
    eventId: asString(dto.eventId) ?? '',
    action: asAction(dto.action),
    subjectId: asString(dto.subjectId),
    occurredAt: asString(dto.occurredAt) ?? '',
    locale: asLocale(dto.locale) ?? DEFAULT_LOCALE,
  };
}

export function mapEventMetrics(dto: ApiEventMetrics): EventMetrics {
  return {
    eventId: asString(dto.eventId) ?? '',
    registrations: asNumber(dto.registrations) ?? 0,
    checkedIn: asNumber(dto.checkedIn) ?? 0,
    checkInsLastHour: asNumber(dto.checkInsLastHour) ?? 0,
    documentDownloads: asNumber(dto.documentDownloads) ?? 0,
    pushDelivered: asNumber(dto.pushDelivered) ?? 0,
    pushOpened: asNumber(dto.pushOpened) ?? 0,
    engagementScore: asNumber(dto.engagementScore) ?? 0,
  };
}

export function mapBroadcast(dto: ApiBroadcast): Broadcast {
  const severity: BroadcastSeverity =
    dto.severity === 'urgent' ? 'urgent' : 'info';

  return {
    id: asString(dto.id) ?? '',
    eventId: asString(dto.eventId) ?? '',
    severity,
    title: asString(dto.title) ?? '',
    body: asString(dto.body) ?? '',
    sentAt: asString(dto.sentAt) ?? '',
    ackCount: asNumber(dto.ackCount) ?? 0,
  };
}

export function mapVelocity(dto: ApiVelocity): CheckInVelocity {
  return {
    eventId: asString(dto.eventId) ?? '',
    bucket:
      dto.bucket === 'minute' || dto.bucket === 'day' ? dto.bucket : 'hour',
    points: (dto.points ?? []).map((point) => ({
      at: asString(point.at) ?? '',
      granted: asNumber(point.granted) ?? 0,
      denied: asNumber(point.denied) ?? 0,
    })),
  };
}

export function mapReferralLink(dto: ApiReferralLink): ReferralLink {
  return {
    attendeeId: dto.attendeeId,
    url: asString(dto.url) ?? '',
    shareCopy: asString(dto.shareCopy) ?? '',
    clicks: asNumber(dto.clicks) ?? 0,
    conversions: asNumber(dto.conversions) ?? 0,
  };
}

export function mapStreamTicket(dto: ApiStreamTicket): MetricsStreamTicket {
  return {
    ticket: asString(dto.ticket) ?? '',
    expiresAt: asString(dto.expiresAt),
  };
}
